// src/oura/merchant/asks.ts
//
// What an ask is to the merchant screens: which ones need them, how late they
// are, and what an unsent answer turns into when it is sent. Pure, so the
// Decisions list, the answers drawer and the answered drawer read one
// definition and never disagree about a count.
//
// "Ask" is the code's word only. On every merchant screen an ask is a
// "question" (YW, 29 Sep 2026: "Your asks" read as the merchant asking), and
// what the merchant starts with Ask for a change is a "request".

import type { ClientDecision, ClientPagePayload, DecisionBatchInput, Pack, PackBlock, PackItem, Thread, ThreadMessage } from "@service/aeo/clientReview";
import type { Tone } from "@oura/ui";
import { dayDate } from "../lib/oura";

/** One ask, with the pack it was sent in. */
export interface Ask {
    item: PackItem;
    pack: Pack;
}

/** Every ask the merchant has been sent, in each pack's own order. A draft pack never reaches them. */
export function asksOf(packs: Pack[]): Ask[] {
    return packs.filter((p) => p.status !== "draft").flatMap((pack) => itemsInOrder(pack).map((item) => ({ item, pack })));
}

/** A pack's items in the order staff laid them out in the note, then any the note does not place. */
function itemsInOrder(pack: Pack): PackItem[] {
    const unplaced = new Map(pack.items.map((i) => [i.id, i]));
    const placed: PackItem[] = [];
    for (const block of pack.blocks) {
        if (block.type !== "decision") continue;
        const item = unplaced.get(block.itemId);
        if (!item) continue;
        placed.push(item);
        unplaced.delete(block.itemId);
    }
    return placed.concat(Array.from(unplaced.values()));
}

/** The note around a pack's asks: its sections, tables and screenshots. */
export function noteBlocks(pack: Pack): Exclude<PackBlock, { type: "decision" }>[] {
    return pack.blocks.filter((b): b is Exclude<PackBlock, { type: "decision" }> => b.type !== "decision");
}

/**
 * Waits on the merchant: not answered yet, or answered with a reply from us
 * waiting. The badge on the Decisions nav item counts by this rule too, so it
 * and the numbers on the screen always match.
 */
export const needsYou = (item: PackItem): boolean => item.decision === "pending" || item.waitingOn === "client";

/** Still to be answered, so it has a card in the answers drawer. */
export const isOpen = (item: PackItem): boolean => item.decision === "pending";

export type AskGroup = "needs" | "revising" | "later" | "answered";

export const ASK_GROUP: Record<AskGroup, { label: string; tone: Tone }> = {
    needs: { label: "Needs your reply", tone: "amber" },
    // They asked for a change, and the next move is ours.
    revising: { label: "Being revised", tone: "blue" },
    // "Not now": greyed like work that has not started.
    later: { label: "Later", tone: "gray" },
    answered: { label: "Answered", tone: "green" },
};
export const ASK_GROUP_ORDER: AskGroup[] = ["needs", "revising", "later", "answered"];

export function askGroup(item: PackItem): AskGroup {
    if (needsYou(item)) return "needs";
    if (item.decision === "changes_requested") return "revising";
    if (item.decision === "deferred") return "later";
    return "answered";
}

/** Missing dates sort last. Dates are ISO strings, so text order is date order. */
function byDate(a: string | null | undefined, b: string | null | undefined): number {
    if (a === b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a < b ? -1 : 1;
}

/**
 * Most pressing first: the furthest past its date, then the soonest due,
 * then the one waiting longest. Lateness is the server's (`lateDays`); an
 * ask without it is simply not ranked by it.
 */
export function byUrgency(a: Ask, b: Ask): number {
    return (
        (b.item.lateDays ?? 0) - (a.item.lateDays ?? 0) ||
        byDate(a.item.replyBy, b.item.replyBy) ||
        byDate(a.pack.publishedAt, b.pack.publishedAt)
    );
}

export const lateDaysOf = (item: PackItem): number => (item.decision === "pending" ? item.lateDays ?? 0 : 0);

/** One fact on a row or in a drawer's status line. `late` draws it in rose. */
export interface TimingPart {
    text: string;
    late?: boolean;
}

/** The newest message from us in an ask's thread, or null. */
export function lastStaffMessage(thread: Thread | null): ThreadMessage | null {
    return [...(thread?.messages ?? [])].reverse().find((m) => m.authorKind === "staff" && m.kind === "message") ?? null;
}

/**
 * The one fact an ask carries, on its row and in its drawer's status line.
 * Merchant lateness is a date, never a day count (specs v1.1, problem 1):
 *
 *   pending and late         "Was due Tue 22 Sep" (late)
 *   pending with a reply-by  "Reply by Fri 2 Oct"
 *   pending, no reply-by     "Asked Fri 18 Sep"
 *   decided, our reply waits "Angel replied Wed 23 Sep", or "A reply is waiting"
 *   anything else            the day it was decided
 *
 * Each clause appears only when the server sent what it rests on.
 */
export function askFact({ item, pack }: Ask, thread: Thread | null): TimingPart | null {
    if (item.decision === "pending") {
        const due = dayDate(item.replyBy);
        if (lateDaysOf(item) > 0) return { text: due ? `Was due ${due}` : "Late", late: true };
        if (due) return { text: `Reply by ${due}` };
        const asked = dayDate(pack.publishedAt);
        return asked ? { text: `Asked ${asked}` } : null;
    }
    if (item.waitingOn === "client") {
        const reply = lastStaffMessage(thread);
        const on = reply ? dayDate(reply.at) : null;
        return { text: reply ? `${reply.authorName} replied${on ? ` ${on}` : ""}` : "A reply is waiting" };
    }
    const decided = dayDate(item.decidedAt);
    return decided ? { text: decided } : null;
}

/** What "Change my answer" takes back, by the answer it takes back. */
export const TAKES_BACK: Record<Exclude<ClientDecision, "pending" | "deferred">, string> = {
    approved: "This takes back your approval.",
    answered: "This takes back your answer.",
    changes_requested: "This takes back the change you asked for.",
};

/**
 * An answer the merchant has started but not sent. Kept per ask for the
 * session, so "Later" and a trip to another screen lose nothing.
 */
export interface AnswerDraft {
    /** An approval: agree as written, or change it. */
    pick?: "agree" | "change";
    /** A free-text answer, or what a change should say. */
    text?: string;
    /** A choice from the ask's options. */
    option?: string;
    /** A file, already uploaded: the handle the decision sends. */
    file?: { fileKey: string; name: string };
    /** Several files, already uploaded, for a document question that asks for more than one ("3 photos"). */
    files?: { fileKey: string; name: string }[];
}

/**
 * What a draft sends, or null while it is not an answer yet. "Send N
 * answers" counts the non-null ones.
 */
export function draftDecision(item: PackItem, draft: AnswerDraft | undefined): DecisionBatchInput | null {
    if (!draft) return null;
    const text = draft.text?.trim() ?? "";
    switch (item.kind) {
        case "approve":
            if (draft.pick === "agree") return { itemId: item.id, decision: "approved" };
            // A change is an answer only once it says what to change. The
            // words go as the decision's comment, which the server posts to
            // the ask's thread for the account manager.
            if (draft.pick === "change" && text) return { itemId: item.id, decision: "changes_requested", comment: text };
            return null;
        case "choose":
            if (item.options && item.options.length > 0) {
                return draft.option ? { itemId: item.id, decision: "answered", answer: draft.option } : null;
            }
            // Words, a photo, or both: the answer call takes the text and the file together.
            if (!text && !draft.file) return null;
            return { itemId: item.id, decision: "answered", ...(text ? { answer: text } : {}), ...(draft.file ? { fileKey: draft.file.fileKey } : {}) };
        case "provide_file":
            // The file itself, by the key the upload returned. A bare file
            // name was all this sent before, and no file ever reached us.
            return draft.file ? { itemId: item.id, decision: "answered", fileKey: draft.file.fileKey } : null;
    }
}

/**
 * Who the answers go to: the account manager, or "ourteam" when nobody is
 * assigned (the server sends null then, #4808).
 */
export const accountManager = (payload: ClientPagePayload): string => payload.preparedBy ?? "ourteam";

/**
 * What the merchant's contact card is called, on Work and on Home: one
 * constant, so the two never drift (YW, 29 Sep 2026, over "Your contact").
 */
export const ACCOUNT_MANAGER_TITLE = "Your account manager";

/** "1 answer", "3 answers". */
export const plural = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** Words that mark a question whose answer ends up where customers see it: a page, a post, a Google profile. */
const PUBLIC_RE = /\b(show|shown|publish|post|posts|page|pages|website|site|google|profile|wording|copy|menu)\b/i;

/**
 * The one line under a public-facing question, so an answer is never
 * mistaken for a post: "Nothing is posted until Angel publishes it. You'll
 * see it in Work." Null for a question nobody outside sees.
 */
export function publicFacingLine(text: string | null | undefined, who: string): string | null {
    return text && PUBLIC_RE.test(text) ? `Nothing is posted until ${who} publishes it. You'll see it in Work.` : null;
}
